import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { Spin, Result, message, Modal } from "antd";
import {
  ClockCircleOutlined,
  IdcardOutlined,
  FileTextOutlined,
  FieldTimeOutlined,
  QuestionCircleOutlined,
  TrophyOutlined,
  UnorderedListOutlined,
  SendOutlined,
  ExclamationCircleOutlined,
  CheckOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import Service from "../../../service";
import "./publicQuizPage.css";

const LOW_TIME_THRESHOLD_SECONDS = 60;

/** Seconds -> "mm:ss", clamped at 0. */
const formatCountdown = (seconds) => {
  const clamped = Math.max(0, seconds);
  const minutes = Math.floor(clamped / 60);
  const secs = clamped % 60;
  return `${String(minutes).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
};

const isAnswerFilled = (value) => value !== undefined && value !== null && value !== "" && !(Array.isArray(value) && value.length === 0);

/** One "icon chip + label/value" row in the Quiz Details sidebar. */
const QuizDetailItem = ({ icon, label, value }) => (
  <div className="quiz-detail-item">
    <div className="quiz-detail-icon">{icon}</div>
    <div className="quiz-detail-content">
      <span className="quiz-detail-label">{label}</span>
      <span className="quiz-detail-value">{value}</span>
    </div>
  </div>
);

/**
 * Renders one screening question as the right plain input control for its
 * type - kept as native form elements (not antd) to match the brand-specific
 * quiz-* CSS classes this public page uses. This page is public/
 * unauthenticated and must never import anything from the recruiter-facing
 * bundle that could leak marks/correct answers (the API response itself
 * already strips those - see controller/public/screeningQuiz.js).
 */
const QuestionInput = ({ question, value, onChange }) => {
  switch (question.question_type) {
    case "Number":
      return (
        <input
          className="quiz-input"
          type="number"
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
        />
      );
    case "Yes/No":
      return (
        <div className="quiz-options">
          {["Yes", "No"].map((option) => (
            <label key={option} className="quiz-option">
              <input type="radio" name={question._id} checked={value === option} onChange={() => onChange(option)} />
              {option}
            </label>
          ))}
        </div>
      );
    case "Dropdown":
      return (
        <select className="quiz-select" value={value || ""} onChange={(e) => onChange(e.target.value)}>
          <option value="" disabled>
            --Select--
          </option>
          {(question.options || []).map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      );
    case "Multiple Choice":
      return (
        <div className="quiz-options">
          {(question.options || []).map((option) => {
            const selected = Array.isArray(value) ? value : [];
            return (
              <label key={option} className="quiz-option">
                <input
                  type="checkbox"
                  checked={selected.includes(option)}
                  onChange={() =>
                    onChange(selected.includes(option) ? selected.filter((item) => item !== option) : [...selected, option])
                  }
                />
                {option}
              </label>
            );
          })}
        </div>
      );
    case "Rating":
      return (
        <div className="rating">
          {[1, 2, 3, 4, 5].map((star) => (
            <span
              key={star}
              className={`rating-star${(value || 0) >= star ? " active" : ""}`}
              onClick={() => onChange(star)}
              role="button"
              tabIndex={0}
            >
              ★
            </span>
          ))}
        </div>
      );
    case "Text":
    default:
      return (
        <input
          className="quiz-input"
          type="text"
          placeholder="Type your answer"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
        />
      );
  }
};

/**
 * Public, unauthenticated quiz page reached via the magic-link token emailed
 * to a candidate (see sendScreeningQuizLink in controller/screening.js). No
 * login, no companyId/candidate identity in the URL - the token alone
 * resolves everything server-side.
 */
const PublicQuizPage = () => {
  const { token } = useParams();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [jobTitle, setJobTitle] = useState("");
  const [quizTitle, setQuizTitle] = useState("Screening Quiz");
  const [companyName, setCompanyName] = useState("");
  const [totalMarks, setTotalMarks] = useState(0);
  const [timeLimitMinutes, setTimeLimitMinutes] = useState(0);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [errors, setErrors] = useState({});
  const [remainingSeconds, setRemainingSeconds] = useState(null);
  const [timeUpModalVisible, setTimeUpModalVisible] = useState(false);
  // null until "Start Quiz" is clicked (server timestamp, see startPublicQuiz).
  // Already set on load after a refresh - the instructions screen is skipped
  // and the countdown resumes from this same anchor instead of restarting.
  const [quizStartedAt, setQuizStartedAt] = useState(null);
  const [starting, setStarting] = useState(false);

  // Guards against the interval firing an auto-submit more than once, and
  // lets the interval always call the latest handleSubmit closure instead of
  // a stale one captured when the effect first ran.
  const autoSubmittedRef = useRef(false);
  const handleSubmitRef = useRef(() => {});
  // Holds a () => seconds getter (set once the quiz start is anchored, see
  // effect below), read at submit time - avoids a handleSubmit dependency
  // and a per-second re-render for a value nothing displays.
  const elapsedSecondsRef = useRef(() => 0);

  const fetchQuiz = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await Service.makeAPICall({
        methodName: Service.getMethod,
        api_url: Service.getPublicQuiz + "/" + token,
      });
      if (response?.data?.status === 200) {
        const data = response.data.data || {};
        setJobTitle(data.job_title || "");
        setQuizTitle(data.quiz_title || "Screening Quiz");
        setCompanyName(data.company_name || "");
        setTotalMarks(data.total_marks || 0);
        setTimeLimitMinutes(data.time_limit_minutes || 0);
        setQuestions(data.questions || []);
        setQuizStartedAt(data.quiz_started_at ? new Date(data.quiz_started_at).getTime() : null);
      } else {
        setError(response?.data?.message || "This quiz link is invalid or has expired.");
        setCompanyName(response?.data?.data?.company_name || "");
      }
    } catch (err) {
      setError("Unable to load this quiz right now. Please try again later.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchQuiz();
  }, [fetchQuiz]);

  const handleStartQuiz = async () => {
    setStarting(true);
    try {
      const response = await Service.makeAPICall({
        methodName: Service.postMethod,
        api_url: Service.startPublicQuiz + "/" + token + "/start",
      });
      if (response?.data?.status === 200) {
        const startedAt = response.data.data?.quiz_started_at;
        setQuizStartedAt(startedAt ? new Date(startedAt).getTime() : Date.now());
      } else {
        message.error(response?.data?.message || "Failed to start the quiz");
      }
    } catch (err) {
      message.error("Failed to start the quiz");
    } finally {
      setStarting(false);
    }
  };

  const setAnswer = (questionId, value) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
    setErrors((prev) => (prev[questionId] ? { ...prev, [questionId]: null } : prev));
  };

  const handleSubmit = useCallback(
    async (force = false) => {
      // Timeout auto-submit must go through even if a required question was
      // left blank - the candidate is out of time either way, and getting
      // stuck on validation would leave the "submitting" modal open forever.
      if (!force) {
        const newErrors = {};
        questions.forEach((question) => {
          if (question.required && !isAnswerFilled(answers[question._id])) {
            newErrors[question._id] = "This question is required";
          }
        });
        if (Object.keys(newErrors).length > 0) {
          setErrors(newErrors);
          return;
        }
      }

      const answersPayload = questions.map((question) => ({
        question_id: question._id,
        question_text: question.question_text,
        question_type: question.question_type,
        answer_value: answers[question._id] ?? null,
      }));

      setSubmitting(true);
      try {
        const response = await Service.makeAPICall({
          methodName: Service.postMethod,
          api_url: Service.submitPublicQuiz + "/" + token + "/submit",
          body: { answers: answersPayload, time_taken_seconds: elapsedSecondsRef.current() },
        });
        setSubmitting(false);
        if (response?.data?.status === 200) {
          setSubmitted(true);
        } else {
          setTimeUpModalVisible(false);
          message.error(response?.data?.message || "Failed to submit quiz");
        }
      } catch (err) {
        setSubmitting(false);
        setTimeUpModalVisible(false);
        message.error("Failed to submit quiz");
      }
    },
    [questions, answers, token]
  );

  useEffect(() => {
    handleSubmitRef.current = handleSubmit;
  }, [handleSubmit]);

  // Anchored to quizStartedAt - the server's own timestamp from
  // startPublicQuiz, not a client-side clock. A refresh re-fetches that same
  // timestamp (see fetchQuiz), so the countdown always resumes from the real
  // remaining time instead of restarting or getting stuck.
  useEffect(() => {
    if (loading || error || submitted || !quizStartedAt) return undefined;

    const startedAt = quizStartedAt;
    // elapsedSecondsRef only needs to be current at submit time - no need to
    // tick a UI-invisible value every second when there's no countdown to
    // drive. Refreshed from startedAt directly wherever it's read instead.
    elapsedSecondsRef.current = () => Math.max(0, Math.round((Date.now() - startedAt) / 1000));

    if (timeLimitMinutes <= 0) return undefined;
    const deadline = startedAt + timeLimitMinutes * 60 * 1000;

    const tick = () => {
      const secondsLeft = Math.round((deadline - Date.now()) / 1000);
      setRemainingSeconds(Math.max(0, secondsLeft));
      if (secondsLeft <= 0 && !autoSubmittedRef.current) {
        autoSubmittedRef.current = true;
        setTimeUpModalVisible(true);
        handleSubmitRef.current(true);
      }
    };

    tick();
    const intervalId = setInterval(tick, 1000);
    return () => clearInterval(intervalId);
  }, [loading, error, submitted, timeLimitMinutes, quizStartedAt]);

  const showCountdown = timeLimitMinutes > 0 && remainingSeconds !== null && !submitted && !error;
  const isLowTime = showCountdown && remainingSeconds <= LOW_TIME_THRESHOLD_SECONDS;
  const answeredCount = questions.filter((question) => isAnswerFilled(answers[question._id])).length;
  const progressPercent = questions.length ? Math.round((answeredCount / questions.length) * 100) : 0;

  if (!loading && !error && submitted) {
    return (
      <div className="quiz-success-page">
        <header className="quiz-success-header">
          <div className="quiz-success-brand">{companyName}</div>
        </header>
        <main className="quiz-success-content">
          <div className="quiz-success-card">
            <div className="quiz-success-icon">
              <CheckOutlined />
            </div>
            <h2 className="quiz-success-title">Quiz Submitted</h2>
            <p className="quiz-success-description">
              Thank you for completing the screening quiz. Your response has been recorded.
            </p>
            <div className="quiz-success-message">
              <CheckCircleOutlined /> Your response has been saved successfully
            </div>
          </div>
        </main>
      </div>
    );
  }

  const quizDetailsSidebar = (
    <aside className="quiz-details">
      <div className="quiz-details-title">
        <div className="quiz-details-title-icon">
          <UnorderedListOutlined />
        </div>
        Quiz Details
      </div>
      {jobTitle && <QuizDetailItem icon={<IdcardOutlined />} label="Position" value={jobTitle} />}
      <QuizDetailItem icon={<FileTextOutlined />} label="Quiz" value={quizTitle} />
      <QuizDetailItem icon={<QuestionCircleOutlined />} label="Questions" value={questions.length || "-"} />
      {totalMarks > 0 && <QuizDetailItem icon={<TrophyOutlined />} label="Total Marks" value={totalMarks} />}
      <QuizDetailItem
        icon={<FieldTimeOutlined />}
        label="Time Limit"
        value={timeLimitMinutes > 0 ? `${timeLimitMinutes} minute${timeLimitMinutes === 1 ? "" : "s"}` : "No limit"}
      />
    </aside>
  );

  return (
    <div className="quiz-page">
      <header className="quiz-header">
        <div className="quiz-brand">{companyName}</div>
        {showCountdown && (
          <div className="quiz-timer">
            <div className="quiz-timer-value" style={{ color: isLowTime ? "#ef4444" : "#173b70" }}>
              <ClockCircleOutlined />
              {formatCountdown(remainingSeconds)}
            </div>
            <span className="quiz-timer-label">Time Remaining</span>
          </div>
        )}
      </header>

      <main className="quiz-content">
        {loading && (
          <div className="quiz-standalone">
            <Spin size="large" />
          </div>
        )}

        {!loading && error && (
          <div className="quiz-standalone">
            <div className="quiz-card">
              <Result status="warning" title="Quiz unavailable" subTitle={error} />
            </div>
          </div>
        )}

        {!loading && !error && quizStartedAt && (
          <>
            <div className="quiz-breadcrumb">
              <span>&rsaquo;</span>
              <span className="quiz-breadcrumb-current">{quizTitle}</span>
            </div>

            <div className="quiz-layout">
              <section className="quiz-card">
                <div className="quiz-title-section">
                  <div className="quiz-title-wrapper">
                    <div className="quiz-title-icon">
                      <FileTextOutlined />
                    </div>
                    <div>
                      <h1 className="quiz-title">{quizTitle}</h1>
                      <p className="quiz-description">Please answer every required question and submit.</p>
                    </div>
                  </div>
                </div>

                <div className="quiz-progress-section">
                  <div className="quiz-progress-info">
                    <span>
                      {answeredCount} of {questions.length} answered
                    </span>
                    <span className="quiz-progress-value">{progressPercent}%</span>
                  </div>
                  <div className="quiz-progress">
                    <div className="quiz-progress-bar" style={{ width: `${progressPercent}%` }} />
                  </div>
                </div>

                <div className="questions-container">
                  {questions.map((question, index) => (
                    <div className="quiz-question" key={question._id}>
                      <div className="quiz-question-header">
                        <span className="question-number">Q{index + 1}</span>
                        <div className="question-content">
                          <label className="question-label">{question.question_text}</label>
                          <QuestionInput
                            question={question}
                            value={answers[question._id]}
                            onChange={(value) => setAnswer(question._id, value)}
                          />
                          {errors[question._id] && <div className="quiz-field-error">{errors[question._id]}</div>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="quiz-submit-wrapper">
                  <button className="quiz-submit" disabled={submitting} onClick={() => handleSubmit()}>
                    <SendOutlined />
                    {submitting ? "Submitting..." : "Submit Quiz"}
                  </button>
                </div>
              </section>

              {quizDetailsSidebar}
            </div>
          </>
        )}
      </main>

      <Modal
        title="Instructions"
        open={!loading && !error && !submitted && !quizStartedAt}
        closable={false}
        maskClosable={false}
        centered
        footer={[
          <button key="start" className="quiz-submit" disabled={starting} onClick={handleStartQuiz}>
            {starting ? "Starting..." : "Start Quiz"}
          </button>,
        ]}
      >
        <ul style={{ paddingLeft: 20, margin: 0, color: "#434343", lineHeight: 2 }}>
          <li>This quiz has {questions.length} question{questions.length === 1 ? "" : "s"}.</li>
          {timeLimitMinutes > 0 && (
            <li>
              You will have <strong>{timeLimitMinutes} minute{timeLimitMinutes === 1 ? "" : "s"}</strong> once you start -
              the timer cannot be paused, and it keeps running even if you refresh or close this page.
            </li>
          )}
          <li>Questions marked required must be answered before you can submit.</li>
          <li>You can only submit once - review your answers before submitting.</li>
          {timeLimitMinutes > 0 && <li>If time runs out, your answers are submitted automatically.</li>}
        </ul>
      </Modal>

      <Modal open={timeUpModalVisible && !submitted} closable={false} footer={null} maskClosable={false} centered>
        <div style={{ textAlign: "center", padding: "12px 0" }}>
          <ExclamationCircleOutlined style={{ fontSize: 40, color: "#faad14", marginBottom: 12 }} />
          <h4 style={{ marginBottom: 4 }}>Time's Up</h4>
          <p style={{ color: "#71829a" }}>Your time has expired. Submitting your answers now, please wait...</p>
          <div style={{ marginTop: 16 }}>
            <Spin />
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default PublicQuizPage;
